<template>
  <app-modal-prompt
    :value="value"
    :loading="loading"
    @input="e => $emit('input', e)"
    @confirm="confirmChanges"
    @cancel="$emit('cancel')"
    :width="width"
  >
    <template #title>
      {{title}}
    </template>

    <template v-if="$slots['header-actions'] || $scopedSlots['header-actions']" #header-actions>
      <slot name="header-actions" :confirm="confirmChanges" />
    </template>

    <template>
      <app-modal-container>
        <app-modal-row v-if="$slots.top_actions">
          <app-modal-col cols="12">
            <slot name="top_actions"></slot>
          </app-modal-col>
        </app-modal-row>

        <app-modal-row class="row-with-center-controls">

          <!-- Left column -->
          <app-modal-col cols="12" sm="5" class="pad-right">
            <v-list dense class="py-0">
              <v-subheader class="text-color sub-header">
                {{ show_columns_header }}
              </v-subheader>

              <!-- Filter only when there are items -->
              <ColumnFilter v-if="show_copy.length" :items="show_copy" placeholder="Filtrar columnas visibles">
                <template v-slot:default="{ filteredItems }">
                  <draggable
                    v-model="show_copy"
                    :options="{ group: 'people' }"
                    style="min-height: 10px"
                    :class="['y-scroll', { 'no-scroll': !show_copy.length }]"
                  >
                    <template v-for="(item, index) in filteredItems">
                      <v-list-item
                        :key="item.value"
                        class="draggable-item pretty-item"
                        :class="{ 'pretty-item--selected': selectedShowItems.includes(item.value) }"
                        @click.stop="toggleShowSelection(item.value)"
                      >
                        <v-list-item-content>
                          <v-list-item-title class="text-color">
                            {{ (index+1) + '. ' + item.text }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </draggable>

                  <!-- Select/Deselect All only when there are items -->
                  <div v-if="show_copy.length" class="list-actions">
                    <v-btn
                      small
                      outlined
                      class="toggle-all"
                      :class="{ 'toggle-all--active': isAllShowSelected }"
                      color="primary"
                      @click="toggleAllShow"
                    >
                      {{ isAllShowSelected ? 'Deseleccionar' : 'Seleccionar todo' }}
                    </v-btn>
                  </div>
                </template>
              </ColumnFilter>

              <!-- Empty placeholder to keep height and hide scroll -->
              <div v-else class="y-scroll no-scroll"></div>
            </v-list>
          </app-modal-col>

          <!-- Center arrows -->
          <app-modal-col cols="12" sm="2" class="middle-col">
            <div class="middle-controls">
              <!-- Right arrow only when left list has items -->
              <v-btn
                v-if="show_copy.length"
                icon small color="primary"
                :disabled="!selectedShowItems.length"
                class="ctrl-btn"
                @click="moveShowToHide"
                aria-label="Mover seleccionados a ocultas"
              >
                <v-icon>mdi-arrow-right-bold</v-icon>
              </v-btn>

              <!-- Left arrow only when right list has items -->
              <v-btn
                v-if="hide_copy.length"
                icon small color="primary"
                :disabled="!selectedHideItems.length"
                class="ctrl-btn"
                @click="moveHideToShow"
                aria-label="Mover seleccionados a visibles"
              >
                <v-icon>mdi-arrow-left-bold</v-icon>
              </v-btn>
            </div>
          </app-modal-col>

          <!-- Right column -->
          <app-modal-col cols="12" sm="5" class="pad-left">
            <v-list dense class="py-0">
              <v-subheader class="text-color sub-header">
                {{ hide_columns_header }}
              </v-subheader>

              <!-- Filter only when there are items -->
              <ColumnFilter v-if="hide_copy.length" :items="hide_copy" placeholder="Filtrar columnas ocultas">
                <template v-slot:default="{ filteredItems }">
                  <draggable
                    v-model="hide_copy"
                    :options="{ group: 'people' }"
                    style="min-height: 10px"
                    :class="['y-scroll', { 'no-scroll': !hide_copy.length }]"
                  >
                    <template v-for="item in filteredItems">
                      <v-list-item
                        :key="item.value"
                        class="draggable-item pretty-item"
                        :class="{ 'pretty-item--selected': selectedHideItems.includes(item.value) }"
                        @click.stop="toggleHideSelection(item.value)"
                      >
                        <v-list-item-content>
                          <v-list-item-title class="text-color">
                            {{ item.text }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </draggable>

                  <!-- Select/Deselect All only when there are items -->
                  <div v-if="hide_copy.length" class="list-actions">
                    <v-btn
                      small
                      outlined
                      class="toggle-all"
                      :class="{ 'toggle-all--active': isAllHideSelected }"
                      color="primary"
                      @click="toggleAllHide"
                    >
                      {{ isAllHideSelected ? 'Deseleccionar' : 'Seleccionar todo' }}
                    </v-btn>
                  </div>
                </template>
              </ColumnFilter>

              <!-- Empty placeholder to keep height and hide scroll -->
              <div v-else class="y-scroll no-scroll"></div>
            </v-list>
          </app-modal-col>

        </app-modal-row>
      </app-modal-container>
    </template>
  </app-modal-prompt>
</template>

<script>
import draggable from "vuedraggable";
import AppModalPrompt from '@/components/layout/AppModalPrompt.vue';
import AppModalCol from '@/components/layout/AppModalCol.vue';
import AppModalContainer from '@/components/layout/AppModalContainer.vue';
import AppModalRow from '@/components/layout/AppModalRow.vue';
import ColumnFilter from '@/components/ColumnFilter.vue'

import { DraggableShowHideDialog } from '@/modules/shared/domain/DraggableShowHideDialog'

export default {
  name: 'DraggableShowHideDialog',
  props: {
    value: { type: Boolean, required: true },
    title: { type: String, required: false },
    show_columns_header: { type: String, default: 'Columnas visibles en orden' },
    hide_columns_header: { type: String, default: 'Columnas ocultas' },
    show_columns: { type: Array, required: true },
    hide_columns: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    width: { type: String, default: '700px' },
  },
  data() {
    return {
      show_copy: [],
      hide_copy: [],
      selectedShowItems: [],
      selectedHideItems: [],
      dialogUtils: null,
    }
  },
  components: {
    AppModalPrompt,
    draggable,
    AppModalCol,
    AppModalContainer,
    AppModalRow,
    ColumnFilter
  },
  created() {
    this.dialogUtils = new DraggableShowHideDialog([])
  },
  mounted() {
    this.show_copy = this.normalize(this.show_columns)
    this.hide_copy = this.normalize(this.hide_columns)
  },
  watch: {
    show_columns(newColumns) { this.show_copy = this.normalize(newColumns) },
    hide_columns(newColumns) { this.hide_copy = this.normalize(newColumns) },
    value(isOpen) {
      if (isOpen) {
        this.selectedShowItems = []
        this.selectedHideItems = []
      }
    }
  },
  computed: {
    isAllShowSelected() {
      if (!this.show_copy.length) return false
      return this.selectedShowItems.length === this.show_copy.length
    },
    isAllHideSelected() {
      if (!this.hide_copy.length) return false
      return this.selectedHideItems.length === this.hide_copy.length
    }
  },
  methods: {
    normalize(columnsList) {
      return this.dialogUtils.normalizeColumns(columnsList)
    },
    toggleShowSelection(columnValue) {
      this.selectedShowItems = this.dialogUtils.toggleSelection(this.selectedShowItems, columnValue)
    },
    toggleHideSelection(columnValue) {
      this.selectedHideItems = this.dialogUtils.toggleSelection(this.selectedHideItems, columnValue)
    },
    moveShowToHide() {
      if (!this.selectedShowItems.length) return
      const result = this.dialogUtils.moveSelected(this.show_copy, this.hide_copy, this.selectedShowItems)
      this.show_copy = result.source
      this.hide_copy = result.target
      this.selectedShowItems = []
    },
    moveHideToShow() {
      if (!this.selectedHideItems.length) return
      const result = this.dialogUtils.moveSelected(this.hide_copy, this.show_copy, this.selectedHideItems)
      this.hide_copy = result.source
      this.show_copy = result.target
      this.selectedHideItems = []
    },
    toggleAllShow() {
      this.selectedShowItems = this.dialogUtils.toggleAll(this.show_copy, this.selectedShowItems)
    },
    toggleAllHide() {
      this.selectedHideItems = this.dialogUtils.toggleAll(this.hide_copy, this.selectedHideItems)
    },
    confirmChanges() {
      // arreglo pepe gotera y otilio, espero que temporal
      const col_show = this.dialogUtils.formatColumnsCSV(this.show_copy)
      const col_hide = this.dialogUtils.formatColumnsCSV(this.hide_copy)
      this.$emit('update', { 'show': col_show, 'hide': col_hide })
    }
  }
}
</script>

<style lang="scss" scoped>
.primary-bckground { background-color: var(--v-primary-base); }
.primary-border { border-color: var(--v-primary-base) !important; }

.draggable-item {
  border: thin solid var(--v-primary-base);
  border-right-style: solid;
  border-bottom-style: solid;
}

.text-color { color: var(--v-primary-base); }
.y-scroll { overflow-y: auto; height: 300px; margin-right: 4px; }
.no-scroll { overflow-y: hidden; }
.sub-header { font-size: large; font-weight: 600; }
.pretty-item { margin: 6px 4px; cursor: pointer; }
.pretty-item--selected { background-color: #ede4f7; }

.row-with-center-controls {
  display: flex;
  align-items: stretch;
}

.pad-right { padding-right: 8px; }
.pad-left  { padding-left: 8px; }

.middle-col {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 56px !important;
  max-width: 56px !important;
}

.row-with-center-controls ::v-deep(.col-sm-5) {
  flex: 0 0 calc((100% - 56px)) !important;
  max-width: calc((100% - 56px)) !important;
}

.row-with-center-controls > :first-child {
  flex-basis: calc((100% - 56px) / 2) !important;
  max-width:  calc((100% - 56px) / 2) !important;
}
.row-with-center-controls > :last-child {
  flex-basis: calc((100% - 56px) / 2) !important;
  max-width:  calc((100% - 56px) / 2) !important;
}

.middle-controls {
  display: grid;
  gap: 6px;
}
.ctrl-btn {
  box-shadow: none;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  border-radius: 6px;
  display: grid;
  place-items: center;
}
.ctrl-btn:hover { background: rgba(102, 51, 153, 0.08); }

.list-actions {
  display: flex;
  justify-content: center;
  padding: 30px 0 4px;
}
.toggle-all {
  text-transform: none;
  font-weight: 600;
  border-color: var(--v-primary-base);
  min-width: 160px;
}
.toggle-all--active {
  background-color: #ede4f7;
  border-color: var(--v-primary-base);
}
</style>
